/** Portaled dropdown listboxes (DropdownSearch, MultiSelectDropdownSearch) set this on their panel. */
export const DROPDOWN_PORTAL_ATTR = 'data-vp-dropdown-panel';

export function isDropdownPortalTarget(target: EventTarget | null) {
  return target instanceof Element && target.closest(`[${DROPDOWN_PORTAL_ATTR}]`) != null;
}

/** Above modal overlay/content (`z-50`). Must set pointer-events so Radix modal body lock does not block clicks. */
export const DROPDOWN_PORTAL_Z_INDEX = 100;

/** Default max height for portaled select/search lists (`max-h-60` = 15rem). */
export const DROPDOWN_DEFAULT_MAX_HEIGHT = 240;

/** Prevent Radix dialog RemoveScroll from cancelling wheel/touch on portaled panels. */
export function stopModalScrollLockPropagation(event: React.UIEvent | Event) {
  event.stopPropagation();
}

/**
 * Position a portaled menu under (or above) an anchor so it stays in the viewport.
 * Returns fixed CSS including `maxHeight` so the list can scroll instead of clipping.
 */
export function dropdownPortalPosition(
  rect: DOMRect,
  options?: {
    maxMenuHeight?: number;
    gap?: number;
    margin?: number;
  }
) {
  const gap = options?.gap ?? 4;
  const margin = options?.margin ?? 8;
  const preferredMax = options?.maxMenuHeight ?? DROPDOWN_DEFAULT_MAX_HEIGHT;
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 768;

  const spaceBelow = Math.max(0, vh - rect.bottom - gap - margin);
  const spaceAbove = Math.max(0, rect.top - gap - margin);
  const openAbove = spaceBelow < Math.min(preferredMax, 160) && spaceAbove > spaceBelow;
  const available = openAbove ? spaceAbove : spaceBelow;
  const maxHeight = Math.max(96, Math.min(preferredMax, available || preferredMax));

  let left = rect.left;
  const width = Math.min(rect.width, vw - margin * 2);
  if (left + width > vw - margin) left = Math.max(margin, vw - margin - width);
  if (left < margin) left = margin;

  const shared = {
    position: 'fixed' as const,
    left,
    width,
    maxHeight,
    zIndex: DROPDOWN_PORTAL_Z_INDEX,
    pointerEvents: 'auto' as const,
  };

  if (openAbove) {
    return {
      ...shared,
      bottom: vh - rect.top + gap,
    };
  }
  return {
    ...shared,
    top: rect.bottom + gap,
  };
}

/** Right-aligned panel under an anchor (menus, action dropdowns). */
export function dropdownPortalPositionEndAligned(
  rect: DOMRect,
  panelWidth: number,
  options?: {
    margin?: number;
    topOffset?: number;
    zIndex?: number;
    placement?: 'above' | 'below';
    align?: 'start' | 'end';
  }
) {
  const margin = options?.margin ?? 8;
  const topOffset = options?.topOffset ?? 4;
  const zIndex = options?.zIndex ?? DROPDOWN_PORTAL_Z_INDEX;
  const placement = options?.placement ?? 'below';
  const align = options?.align ?? 'end';
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 768;
  const width = Math.min(panelWidth, vw - margin * 2);
  let left = align === 'end' ? rect.right - width : rect.left;
  if (left < margin) left = margin;
  if (left + width > vw - margin) left = vw - margin - width;
  const shared = {
    position: 'fixed' as const,
    left,
    width,
    zIndex,
    pointerEvents: 'auto' as const,
  };
  if (placement === 'above') {
    return {
      ...shared,
      bottom: vh - rect.top + topOffset,
    };
  }
  return {
    ...shared,
    top: rect.bottom + topOffset,
  };
}
